import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import SignedOut from 'components_v2/Home/SignedOut'
import ClassView from 'components_v1/ClassView/ClassView'
import ProgramView from 'components_v1/ProgramView/ProgramView'
import SignedInHome from 'components_v2/Home/SignedIn/Home'
import TempEntry from '../TempEntry'
import InstructorView from 'components_v1/InstructorView/InstructorView'
import ClassFinderView from 'components_v1/ClassFinderView/ClassFinderView'
import ExploreView from 'components_v1/ExploreView/ExploreView'
import MyPracticeView from 'components_v1/MyPracticeView/MyPracticeView'
import SupportView from 'components_v1/SupportView/SupportView'
import SupportDetails from 'components_v1/SupportView/SupportDetailsView'
import ArticleItemView from 'components_v1/SupportView/ArticleItemView'
import SearchView from 'components_v1/SearchView/SearchView'
import SignIn from 'components_v2/SignIn/SignIn'
import TagsView from 'components_v1/TagsView/TagsView'
import CoachInfoView from 'components_v1/CoachInfoView/CoachInfoView'
import { useMeta } from 'hooks/useMetaTitle'
import PlaylistDetailsView from 'components_v1/PlaylistDetailsView/PlaylistDetailsView'
import EventsView from 'components_v1/EventsView/EventsView'
import EventDetailView from 'components_v1/EventsView/EventDetailView'
import { CommonEntryContext } from 'components_v2/page_context'

const routes = [
  {
    path: '/series/:program_id/workouts/:plan_entry_id',
    Component: ClassView
  },
  {
    path: '/series/:program_id/workouts',
    Component: ProgramView,
    listProp: { initTab: 'workouts' }
  },
  { path: '/series/:program_id', Component: ProgramView },
  { path: '/instructors/:id', Component: InstructorView },
  { path: '/class-finder', Component: ClassFinderView },
  { path: '/explore/instructors', Component: CoachInfoView },
  { path: '/explore/filter', Component: ExploreView },
  { path: '/explore', Component: ExploreView },
  { path: '/my/practice', Component: MyPracticeView, auth: true },
  { path: '/support/:category/:slug', Component: ArticleItemView },
  { path: '/support/:category', Component: SupportDetails },
  { path: '/support', Component: SupportView },
  { path: '/tags/:id', Component: TagsView },
  { path: '/playlists/:slug', Component: PlaylistDetailsView },
  { path: '/events/:id', Component: EventDetailView },
  { path: '/events', Component: EventsView },
  { path: '/search', Component: SearchView, noProps: true }
]

const CommonEntry = ({ user = null, ...initProps }) => {
  const [props, setProps] = useState(initProps || {})
  const [globalState, setState] = useState({})
  const [userState, setUserState] = useState(null)
  const initnotFound = initProps?.notFound || false
  const initnotAvailable = initProps?.notAvailable || false
  const [notAvailable, setNotAvailable] = useState(initnotAvailable)
  const [notFound, setNotFound] = useState(initnotFound)
  const updateUser = useCallback(
    (k, v) =>
      setUserState((state) => {
        if (state) {
          return { ...state, [k]: v }
        }
        return state
      }),
    []
  )
  useEffect(() => {
    setNotAvailable(initnotAvailable)
  }, [initnotAvailable])
  useEffect(() => {
    setNotFound(initnotFound)
  }, [initnotFound])

  useEffect(() => {
    userState === null && setUserState(user)
  }, [user, userState])
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const setGlobalState = useCallback(
    (key, val) => setState((state) => ({ ...state, [key]: val })),
    []
  )
  useMeta([{ canonical: true }])
  const [searchParams, setSearchParams] = useState(params)
  useEffect(() => setSearchParams(params), [params])
  useEffect(() => {
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args)
      window.dispatchEvent(new Event('locationchange'))
    }

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args)
      window.dispatchEvent(new Event('locationchange'))
    }

    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search)
      setSearchParams(params)
      setProps(user ? { user } : {})
      setNotFound(false)
      setNotAvailable(false)
    }
    window.addEventListener('popstate', handleUrlChange)
    window.addEventListener('locationchange', handleUrlChange)

    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      window.removeEventListener('popstate', handleUrlChange)
      window.removeEventListener('locationchange', handleUrlChange)
    }
  }, [props, user])

  return (
    <CommonEntryContext.Provider
      value={{
        user: userState || user,
        searchParams,
        setGlobalState,
        globalState,
        setNotFound,
        updateUser
      }}
    >
      <BrowserRouter>
        <TempEntry
          notAvailable={notAvailable}
          notFound={notFound}
          mt='0px'
          deprecated
          user={user || null}
        >
          <Routes>
            <Route
              path='/'
              element={user ? <SignedInHome /> : <SignedOut {...props} />}
            />

            {routes.map(
              ({ path, Component, noProps, listProp = {}, auth = false }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    auth && !user ? (
                      <Navigate to='/signin' replace />
                    ) : (
                      <>
                        {noProps ? (
                          <Component />
                        ) : (
                          <Component {...{ ...listProp, ...props }} />
                        )}
                      </>
                    )
                  }
                />
              )
            )}

            <Route
              path='/signin'
              element={
                user ? <Navigate to='/' replace /> : <SignIn {...props} />
              }
            />

            <Route path='*' element={<Navigate to='/' replace />} />
          </Routes>
        </TempEntry>
      </BrowserRouter>
    </CommonEntryContext.Provider>
  )
}

export default CommonEntry
